import { useRef } from "react";

import { Box, Button, Typography } from "@mui/material";
import { DataGrid } from "devextreme-react";
import {
  Column,
  Export,
  Paging,
} from "devextreme-react/data-grid";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";

import { PageProps } from "@ezhou/core";
import { MainCard } from "@ezhou/app_mantis_1_1";

import { JeExportByPostingDateModel } from "./model/jeExport";

type ReviewExportConfirmationProps = {
  pageProps: PageProps;
  jeExport: JeExportByPostingDateModel;
};

export const ReviewExportConfirmation = (props: ReviewExportConfirmationProps) => {
  const dataGridRef = useRef<DataGrid>(null);

  const gridData = props.jeExport.exports?.map((x) => {
    return { ...x.export, sources: x.sources };
  });

  const handleDownloadExcel = () => {
    const dataGridInstance = dataGridRef.current?.instance;
    if (!dataGridInstance) return;

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Journal Entry Exports");

    const postingDate = props.jeExport.postingDate
      ? new Date(props.jeExport.postingDate)
      : undefined;
    const postingDateSuffix = postingDate
      ? `${postingDate.getFullYear()}-${String(postingDate.getMonth() + 1).padStart(2, "0")}-${String(postingDate.getDate()).padStart(2, "0")}`
      : "";
    const fileName = `JournalEntryExports${postingDateSuffix}.xlsx`;

    exportDataGrid({
      component: dataGridInstance,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer as any], { type: "application/octet-stream" }),
          fileName,
        );
      });
    });
  };

  return (
    <MainCard title={props.pageProps.appUtil.getLocaleString("confirmation")}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          {props.pageProps.appUtil.getLocaleString("exportConfirmationTitle")}
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {props.pageProps.appUtil.getLocaleString("exportConfirmationMessage")}
        </Typography>
        <Button variant="contained" color="primary" onClick={handleDownloadExcel}>
          {props.pageProps.appUtil.getLocaleString("exportConfirmationDownload")}
        </Button>
      </Box>
      <DataGrid
        ref={dataGridRef}
        dataSource={gridData}
        height={400}
        showBorders={true}
        allowColumnResizing={true}
        columnResizingMode="widget"
        columnMinWidth={50}
        columnAutoWidth={true}
        style={{ position: "absolute", left: "-9999px" }}
      >
        <Paging defaultPageSize={1000} />
        <Export enabled={false} />
        {gridData && gridData.length > 0 && buildExportColumns(gridData[0])}
      </DataGrid>
    </MainCard>
  );
};

const buildExportColumns = (data: any) => {
  const keys: string[] = [];
  for (const key in data) {
    if (key !== "sources") {
      keys.push(key);
    }
  }
  return keys.map((key: string, index: number) => {
    return <Column key={index} caption={key} dataField={key} />;
  });
};
